import Layout from 'components/layout';

import React, { FC, PropsWithChildren } from 'react';

import { getI18nData, getProcessedValue, getEntityFields } from '@lucid/utils';
import { graphql, HeadFC } from 'gatsby';
import {
  Box,
  ComponentImage,
  ComponentLink,
  Container,
  Heading,
  Hero,
  Icon,
  Link,
  lucidParser,
  LucidHead,
} from '@lucid/components-react';
import reactHTMLParser from 'html-react-parser';
import type { Drupal_NodeWhitePaper, Drupal_ParagraphTopHeroWithLogo } from 'generated/drupal-graphql';
import { mqMin } from '@lucid/theme';
import styled from '@emotion/styled';

import type { Lucid, Template } from 'types/types';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';

const WhitePaper: FC<PropsWithChildren<Template.PageProps>> = ({ data, pageContext }) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeWhitePaper;
  const { entityTranslations } = getEntityFields(node);
  const { shortLangPrefix, pageType, nid, i18nKey, shouldHideSsr } = pageContext;
  const { body, fieldHeroSection, fieldHasWhitepaperCta, fieldHasRssLink, fieldHasFooter, fieldHasNavbar, fieldTitle } =
    node;

  const i18n = getI18nData(i18nData, i18nKey);

  const buttonGroupMx = 15;
  const buttonGroupMy = 10;
  const ButtonGroup = styled(Box)({
    display: 'inline-flex',
    flexWrap: 'wrap',
    margin: `-${buttonGroupMy}px 0 0 -${buttonGroupMx}px`,
    width: `calc(100% + ${buttonGroupMx}px)`,
    '> a.link': { margin: `${buttonGroupMy}px 0 0 ${buttonGroupMx}px` },
  });

  const heroSection = fieldHeroSection?.entity as Drupal_ParagraphTopHeroWithLogo;

  return (
    <Layout
      entityTranslations={entityTranslations}
      hasFooter={fieldHasFooter}
      hasNavbar={fieldHasNavbar}
      nid={nid}
      pageType={pageType}
      shortLangPrefix={shortLangPrefix}
      shouldHideSsr={shouldHideSsr}
    >
      <Container
        css={(theme: Lucid.Theme) => ({
          paddingTop: theme.space.s24,
          paddingBottom: theme.space.s24,
          [mqMin.mobile]: {
            paddingTop: theme.space.s40,
            paddingBottom: theme.space.s40,
          },
        })}
      >
        {heroSection?.fieldParagraph && (
          <Hero
            cta={
              {
                linkText: heroSection?.fieldCtaLink?.title,
                linkUrl: heroSection?.fieldCtaLink?.url?.path,
              } as ComponentLink
            }
            header={heroSection?.fieldTitle as string}
            image={
              {
                imageAlt: heroSection.fieldTitle,
                imageSrc: heroSection.fieldImageLink,
              } as ComponentImage
            }
            subheader={heroSection.fieldParagraph as string}
          />
        )}
        <Heading variant="headline3XL">{fieldTitle}</Heading>
        {fieldHasWhitepaperCta && (
          <Box as="section">
            <Heading as="h2" variant="headlineL">
              {i18n('mktg-ct-cs-cta-header')}
            </Heading>
            <ButtonGroup>
              <SignUpCta>{i18n('mktg-ct-og-feature-paragraph-cta')}</SignUpCta>
              <Link displayAsButton to="/contact/get-a-quote">
                {i18n('mktg-ct-solutions-footer-cta-primary')}
              </Link>
            </ButtonGroup>
          </Box>
        )}
        {body && (
          <Box
            css={(theme: Lucid.Theme) => [
              theme.shared.htmlParserStyles,
              {
                'p, li': {
                  fontSize: theme.fontSizes.fs16,
                },
                width: '100%',
                wordBreak: 'break-word',
                tbody: {
                  minWidth: '500px',
                  overflow: 'scroll',
                },
                th: {
                  background: theme.colors.coolGrey[20],
                  textAlign: 'left',
                },
                td: {
                  border: `1px solid ${theme.colors.coolGrey[30]}`,
                  padding: '0 5px',
                  wordBreak: 'normal',
                },
                a: {
                  color: theme.colors.primary,
                },
                'ol, ul': {
                  '> li': {
                    marginBottom: '10px',
                  },
                  'ul, ol': {
                    padding: `${theme.space.s16}px ${theme.space.s32}px`,
                  },
                },
              },
            ]}
          >
            {reactHTMLParser(getProcessedValue(body), {
              replace: lucidParser.link,
            })}
          </Box>
        )}
        {fieldHasRssLink && (
          <Box as="section">
            <Heading as="h2" mt={6} variant="headlineL">
              {i18n('mtkg-whitepaper-rss')}
            </Heading>
            <Link displayAsButton newTab to="/legal-rss.xml">
              <Icon type="RSS" />
              &nbsp;&nbsp;RSS
            </Link>
          </Box>
        )}
        {fieldHasWhitepaperCta && (
          <Box as="section">
            <Heading as="h2" mt={6} variant="headlineL">
              {i18n('mktg-ct-cs-cta-header')}
            </Heading>
            <ButtonGroup>
              <SignUpCta>{i18n('mktg-ct-og-feature-paragraph-cta')}</SignUpCta>
              <Link displayAsButton to="/contact/get-a-quote" variant="secondary">
                {i18n('mktg-ct-solutions-footer-cta-primary')}
              </Link>
            </ButtonGroup>
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default WhitePaper;

export const Head: HeadFC<Template.PageProps['data'], Template.PageProps['pageContext']> = ({ ...props }) => (
  <LucidHead<Drupal_NodeWhitePaper> {...props} />
);

export const query = graphql`
  query ($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeWhitePaper {
          ...entityFields
          fieldTitle
          fieldNavbarLink {
            title
            url {
              path
            }
          }
          fieldHeroSection {
            entity {
              ... on Drupal_ParagraphTopHeroWithLogo {
                fieldTitle
                fieldImageLink
                fieldParagraph
                fieldCtaLink {
                  title
                  url {
                    path
                  }
                }
              }
            }
          }
          body {
            processed
          }
          fieldHasWhitepaperCta
          fieldHasNavbar
          fieldHasFooter
          fieldHasRssLink
        }
      }
    }
    i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" }, name: { in: [$i18nKey, "en"] } }) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_cs_cta_header
            mktg_ct_og_feature_paragraph_cta
            mktg_ct_solutions_footer_cta_primary
            mtkg_whitepaper_rss
          }
        }
      }
    }
  }
`;
